import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';
import type { WebEvent } from '../types/events';
import type {
  CreateWebInviteInput,
  CreatedWebInvite,
  WebEventMember,
  WebEventPermissionSummary,
  WebGuestInvite,
  WebJoinRequest,
  WebMemberRole,
  WebPeopleSnapshot,
} from '../types/people';

const INVITE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function normalizePermission(data: unknown): WebEventPermissionSummary | null {
  const value = Array.isArray(data) ? data[0] : data;
  return value && typeof value === 'object' ? (value as WebEventPermissionSummary) : null;
}

export function canManageEventPeople(permission: WebEventPermissionSummary | null | undefined): boolean {
  return Boolean(permission?.canManagePeople || permission?.isOwner || permission?.isAppAdmin);
}

export function canChangeEventRoles(permission: WebEventPermissionSummary | null | undefined): boolean {
  return Boolean(permission?.isOwner || permission?.isAppAdmin);
}

export function formatInviteCode(code: string): string {
  const clean = String(code ?? '').replace(/[^A-Z0-9]/gi, '').toUpperCase();
  if (clean.length <= 6) return `${clean.slice(0, 3)}-${clean.slice(3)}`;
  return `${clean.slice(0, 4)}-${clean.slice(4)}`;
}

function generateInviteCode(): string {
  const bytes = new Uint32Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => INVITE_CHARS[value % INVITE_CHARS.length]).join('');
}

function buildInviteMessage(eventName: string, inviteCode: string, role: WebMemberRole): string {
  const roleLabel = role === 'co_admin' ? 'co-admin' : 'guest';
  return `You're invited to ${eventName || 'an event'} on Gathrd as a ${roleLabel}.\n\nInvite code: ${formatInviteCode(inviteCode)}\n\nOpen Gathrd, choose Join Event, and enter this invite code. Shared moments, together.`;
}

async function loadPermission(eventId: string): Promise<WebEventPermissionSummary | null> {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('get_my_event_permission_summary', {
    p_event_id: eventId,
  });
  if (error) {
    console.warn('[web people] permission summary failed:', error.message);
    return null;
  }
  return normalizePermission(data);
}

export async function loadPeopleSnapshot(eventId: string): Promise<WebPeopleSnapshot> {
  const client = getSupabaseClient();
  const permissions = await loadPermission(eventId);
  const canManage = canManageEventPeople(permissions);

  const membersPromise = client
    .from('event_members')
    .select('event_id,user_id,role,joined_at,profile:profiles(id,full_name,email,avatar_url)')
    .eq('event_id', eventId)
    .order('joined_at', { ascending: false });

  const invitesPromise = canManage
    ? client.from('guest_invites').select('*').eq('event_id', eventId).order('invited_at', { ascending: false })
    : Promise.resolve({ data: [], error: null });

  const requestsPromise = canManage
    ? client
        .from('join_requests')
        .select('id,event_id,user_id,status,created_at,reviewed_at,profile:profiles(id,full_name,email,avatar_url)')
        .eq('event_id', eventId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
    : Promise.resolve({ data: [], error: null });

  const [membersResult, invitesResult, requestsResult] = await Promise.all([
    membersPromise,
    invitesPromise,
    requestsPromise,
  ]);

  if (membersResult.error) throw membersResult.error;
  if (invitesResult.error) throw invitesResult.error;
  if (requestsResult.error) throw requestsResult.error;

  const members: WebEventMember[] = ((membersResult.data ?? []) as unknown as WebEventMember[]).map((member) => ({
    ...member,
    profile: Array.isArray(member.profile) ? member.profile[0] ?? null : member.profile ?? null,
    role: (member.role === 'co_admin' ? 'co_admin' : 'member') as WebMemberRole,
  }));
  const invites = (invitesResult.data ?? []) as unknown as WebGuestInvite[];
  const requests = ((requestsResult.data ?? []) as unknown as WebJoinRequest[]).map((request) => ({
    ...request,
    profile: Array.isArray(request.profile) ? request.profile[0] ?? null : request.profile ?? null,
  }));

  return { members, invites, requests, permissions };
}

async function checkPeoplePlanLimits(
  eventId: string,
  pending: { guests?: number; invites?: number; coAdmins?: number },
) {
  const client = getSupabaseClient();
  const { data, error } = await client.rpc('check_event_plan_limits', {
    p_event_id: eventId,
    p_add_photos: 0,
    p_add_videos: 0,
    p_add_guests: pending.guests ?? 0,
    p_add_invites: pending.invites ?? 0,
    p_add_co_admins: pending.coAdmins ?? 0,
    p_add_bytes: 0,
  });
  if (error) {
    console.warn('[web people] plan limit check failed:', error.message);
    return null;
  }
  return (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
}

function planLimitMessage(check: Record<string, unknown> | null): string | null {
  if (!check || check.allowed === true) return null;
  const reason = String(check.reason ?? '');
  const label = String(check.plan_label ?? 'This event package');
  if (reason === 'guest_limit_reached') return `${label} has reached its guest limit.`;
  if (reason === 'invite_limit_reached') return `${label} has reached its invite limit.`;
  if (reason === 'co_admin_limit_reached') return `${label} has reached its co-admin limit.`;
  return 'This people-management action is not allowed by the current event package.';
}

export async function createGuestInvite(
  event: WebEvent,
  inviterUserId: string,
  input: CreateWebInviteInput,
): Promise<CreatedWebInvite> {
  const client = getSupabaseClient();
  const permissions = await loadPermission(event.id);
  if (!canManageEventPeople(permissions)) {
    throw new Error('Only the host or a co-admin can invite people to this event.');
  }

  const recipient = String(input.recipient ?? '').trim();
  if (input.method !== 'share' && !recipient) {
    throw new Error(input.method === 'email' ? 'Enter an email address.' : 'Enter a phone number.');
  }

  const limitCheck = await checkPeoplePlanLimits(event.id, {
    invites: 1,
    coAdmins: input.role === 'co_admin' ? 1 : 0,
  });
  const limitMessage = planLimitMessage(limitCheck);
  if (limitMessage) throw new Error(limitMessage);

  const inviteCode = generateInviteCode();
  const row: Record<string, unknown> = {
    event_id: event.id,
    full_name: String(input.fullName ?? '').trim(),
    status: 'invited',
    role_to_assign: input.role,
    invite_code: inviteCode,
    invited_by: inviterUserId,
    expires_at: event.expires_at ?? null,
  };
  if (input.method === 'email') row.email = recipient.toLowerCase();
  if (input.method === 'sms') row.phone = recipient;

  const query = input.method === 'email'
    ? client.from('guest_invites').upsert(row, { onConflict: 'event_id,email' }).select('*').single()
    : client.from('guest_invites').insert(row).select('*').single();
  const { data, error } = await query;
  if (error) throw error;

  return {
    invite: data as WebGuestInvite,
    message: buildInviteMessage(event.name, inviteCode, input.role),
  };
}

export async function changeEventMemberRole(
  eventId: string,
  member: WebEventMember,
  nextRole: WebMemberRole,
): Promise<void> {
  const client = getSupabaseClient();
  const permissions = await loadPermission(eventId);
  if (!canChangeEventRoles(permissions)) {
    throw new Error('Only the event owner can change member roles.');
  }

  if (nextRole === 'co_admin' && member.role !== 'co_admin') {
    const limitCheck = await checkPeoplePlanLimits(eventId, { coAdmins: 1 });
    const limitMessage = planLimitMessage(limitCheck);
    if (limitMessage) throw new Error(limitMessage);
  }

  const { error } = await client
    .from('event_members')
    .update({ role: nextRole })
    .eq('event_id', eventId)
    .eq('user_id', member.user_id);
  if (error) throw error;
}

export async function removeEventMember(eventId: string, userId: string): Promise<void> {
  const client = getSupabaseClient();
  const permissions = await loadPermission(eventId);
  if (!canManageEventPeople(permissions)) {
    throw new Error('Only the host or a co-admin can remove people from this event.');
  }
  const { error } = await client
    .from('event_members')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', userId);
  if (error) throw error;
}

async function bestEffortPush(params: {
  toUserId: string;
  title: string;
  body: string;
  eventId: string;
  screen: string;
}) {
  try {
    const client = getSupabaseClient();
    const { error } = await client.functions.invoke('send-push-notification', {
      body: {
        toUserId: params.toUserId,
        title: params.title,
        body: params.body,
        data: { screen: params.screen, eventId: params.eventId },
      },
    });
    if (error) console.warn('[web people] push notification failed:', error.message);
  } catch (error) {
    console.warn('[web people] push notification failed:', error);
  }
}

export async function reviewJoinRequest(params: {
  eventId: string;
  eventName: string;
  request: WebJoinRequest;
  approve: boolean;
  actorUserId: string;
}): Promise<void> {
  const client = getSupabaseClient();
  const permissions = await loadPermission(params.eventId);
  if (!canManageEventPeople(permissions)) {
    throw new Error('Only the host or a co-admin can review join requests.');
  }

  if (params.approve) {
    const limitCheck = await checkPeoplePlanLimits(params.eventId, { guests: 1 });
    const limitMessage = planLimitMessage(limitCheck);
    if (limitMessage) throw new Error(limitMessage);
  }

  const { error: requestError } = await client
    .from('join_requests')
    .update({
      status: params.approve ? 'approved' : 'denied',
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', params.request.id);
  if (requestError) throw requestError;

  if (params.approve) {
    const { error: memberError } = await client
      .from('event_members')
      .insert({ event_id: params.eventId, user_id: params.request.user_id });
    if (memberError) throw memberError;
  }

  await client.from('notifications').insert({
    user_id: params.request.user_id,
    type: params.approve ? 'request_approved' : 'request_denied',
    event_id: params.eventId,
    actor_id: params.actorUserId,
  });

  void bestEffortPush({
    toUserId: params.request.user_id,
    title: params.approve ? "You're in! 🎉" : 'Request not approved',
    body: params.approve
      ? `Your request to join "${params.eventName}" was approved.`
      : `Your request to join "${params.eventName}" was not approved.`,
    eventId: params.eventId,
    screen: params.approve ? 'event' : 'home',
  });
}

export function subscribeToPeopleChanges(eventId: string, onChange: () => void): () => void {
  const client = getSupabaseClient();
  let channel: RealtimeChannel = client
    .channel(`web-people-${eventId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'event_members', filter: `event_id=eq.${eventId}` }, onChange)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'guest_invites', filter: `event_id=eq.${eventId}` }, onChange)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'join_requests', filter: `event_id=eq.${eventId}` }, onChange);

  channel = channel.subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}
